import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { request } from '@/api/http'
import type { ContextWarning } from '@/types/nf'
import { jobApi } from '@/api/jobApi'

export function JobContextWarnings({ jobId }: { jobId: string }) {
  const { data, isError } = useQuery({ queryKey: ['job-context', jobId], queryFn: () => jobApi.get(jobId) })
  return <ContextWarnings warnings={data?.context_warnings} isError={isError} />
}

export function ChapterContextWarnings({ chapterId }: { chapterId: string }) {
  const { data, isError } = useQuery({
    queryKey: ['chapter-context', chapterId],
    queryFn: () => request<{ context_warnings: ContextWarning[] }>(`/v1/chapters/${encodeURIComponent(chapterId)}/context-warnings`),
  })
  return <ContextWarnings warnings={data?.context_warnings} isError={isError} />
}

function ContextWarnings({ warnings, isError }: { warnings?: ContextWarning[]; isError: boolean }) {
  const { t } = useTranslation()
  if (isError) return <p role="alert">{t('contextWarnings.failed')}</p>
  if (!warnings?.length) return null
  return <aside className="rounded-lg border bg-muted p-4 text-sm" role="status">
    <h3 className="font-semibold">{t('contextWarnings.title')}</h3>
    <ul className="mt-2 space-y-2">
      {warnings.map((part, index) => part.reason === 'context_warnings_unrecorded' ? <li key={index}>{t('contextWarnings.unrecorded')}</li> : <li key={index}>
        <span className="font-medium">{t(`contextWarnings.sources.${part.source}`, { defaultValue: part.source })}</span>
        {' — '}{t(part.included ? 'contextWarnings.truncated' : 'contextWarnings.dropped')}: {t(`contextWarnings.reasons.${part.reason}`, { defaultValue: t('contextWarnings.other') })}
      </li>)}
    </ul>
  </aside>
}
